import baseStyles from "./BaseInputs.module.css";
import { EditorV3, EditorV3Props } from "./EditorV3";

interface BaseEdivotV3Props extends EditorV3Props {
  label?: string;
  errorText?: string;
}

/**
 * Base editor wrapper that initialises editor configuration and provides
 * core context for editor instances.
 *
 * @param props - Destructured parameter object
 * @param props.label - Optional label text shown above the editor input.
 * @param props.errorText - Optional multiline error message displayed above the editor.
 * @param props.style - Optional inline styles applied to the editor container.
 * @param props.resize - Controls whether the editor container is resizable.
 *        Accepts `true` (allow vertical resizing), the string `"vertical"`
 *        (vertical resize only), or `false` to disable resizing. When enabled,
 *        the outer container will show a vertical resize handle and allow
 *        overflow scrolling.
 */
export const BaseEditorV3 = ({
  label,
  errorText,
  style,
  resize,
  ...rest
}: BaseEdivotV3Props): React.ReactElement => {
  const outerStyle: React.CSSProperties = {};
  const innerStyle: React.CSSProperties = { ...style };
  /* istanbul ignore else */
  if (style) {
    /* istanbul ignore else */
    if (style.width) {
      outerStyle.width = style.width;
      innerStyle.width = "100%";
    }
    /* istanbul ignore else */
    if (style.maxWidth) {
      outerStyle.maxWidth = style.maxWidth;
      innerStyle.maxWidth = "100%";
    }
    /* istanbul ignore else */
    if (style.minWidth) {
      outerStyle.minWidth = style.minWidth;
      innerStyle.minWidth = "100%";
    }
    /* istanbul ignore else */
    if (style.height) {
      outerStyle.height = style.height;
      innerStyle.height = "100%";
    } else {
      outerStyle.height = "calc(1.5em + 26px)";
      innerStyle.height = "100%";
    }
    /* istanbul ignore else */
    if (style.maxHeight) {
      outerStyle.maxHeight = style.maxHeight;
      innerStyle.maxHeight = "100%";
    }
    /* istanbul ignore else */
    if (style.minHeight) {
      outerStyle.minHeight = style.minHeight;
      innerStyle.minHeight = "100%";
    } else {
      outerStyle.minHeight = "calc(1.5em + 26px)";
      innerStyle.minHeight = "100%";
    }
    /* istanbul ignore else */
    if (resize === true || resize === "vertical") {
      outerStyle.resize = "vertical";
      outerStyle.overflow = "auto";
    }
  }

  return (
    <div
      className={baseStyles.holder}
      style={outerStyle}
    >
      {label !== undefined && (
        <label
          className={baseStyles.label}
          id={`${rest.id}-label`}
          htmlFor={`${rest.id}`}
        >
          {label}
        </label>
      )}
      {errorText !== undefined && errorText.trim() !== "" && (
        <div className={baseStyles.errorText}>
          <>
            {errorText.split("\n").map((errorLine, ix) => (
              <div key={ix}>{errorLine}</div>
            ))}
          </>
        </div>
      )}
      <div className={baseStyles.editorHolder}>
        <EditorV3
          {...rest}
          aria-labelledby={`${rest.id}-label`}
          style={innerStyle}
          noBorder
          resize={false}
        />
      </div>
    </div>
  );
};

BaseEditorV3.displayName = "BaseEditorV3";
